import { Formik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeComponents } from '../../../../../themes';
import useHandleError from '../../../../../utilities/useHandleError';
import useValidator from '../../../../../utilities/useValidator';
import useAgreementsValidator from '../../../../shared/CIPage/ConsentAndDeclarations/useAgreementsValidator';
import useAgreementsValues, {
    type AgreementValues,
} from '../../../../shared/CIPage/ConsentAndDeclarations/useAgreementsValues';
import {
    type PaymentData,
    TransactionStatus,
} from '../../../StandardApplicationEntrypoint/PorschePaymentPage/PorschePaymentWidget';
import useGiftVoucherPaymentAgreements from '../AdyenPaymentPage/useGiftVoucherPaymentAgreements';
import Inner, { type GiftVoucherPorschePaymentInnerProps } from './Inner';
import useGiftVoucherPorscheDepositSubmission, { submitPayment } from './useGiftVoucherPorscheDepositSubmission';

type PorschePaymentFormValues = AgreementValues & {
    paymentData: PaymentData;
    promoCodeId?: string;
};

const SubmittedState = new Set([TransactionStatus.CAPTURED, TransactionStatus.CAPTURE_REQUEST_SENT]);

const GiftVoucherPorschePaymentPage = (
    props: Omit<GiftVoucherPorschePaymentInnerProps, 'SkipDepositButton' | 'canSkipDeposit' | 'isSuccessful'>
) => {
    const { state, dispatch } = props;
    const { token } = state;
    const { t } = useTranslation(['paymentDetails']);
    const { notification } = useThemeComponents();

    const paymentAgreements = useGiftVoucherPaymentAgreements(state);
    const initialValues = useAgreementsValues(paymentAgreements);
    const validator = useAgreementsValidator(paymentAgreements);
    const validation = useValidator(validator);

    const [transactionStatus, setTransactionStatus] = useState<TransactionStatus | null>(null);

    const isSuccessful =
        state.giftVoucher.draftFlow.isDepositCompleted || (transactionStatus && SubmittedState.has(transactionStatus));

    const submitPorscheDeposit = useGiftVoucherPorscheDepositSubmission();
    const onSubmit = useHandleError(
        async (values: PorschePaymentFormValues, helpers) => {
            try {
                notification.loading({
                    content: t('paymentDetails:messages.paymentSubmitting'),
                    duration: 0,
                    key: 'primary',
                });

                const { paymentData, promoCodeId, ...agreements } = values;
                // call payment submission api
                const paymentResult = await submitPayment(paymentData, agreements, token, promoCodeId);

                const { redirectUrl, status } = paymentResult;

                switch (status) {
                    case TransactionStatus.FAILED: {
                        notification.warn(t('paymentDetails:resultCode.error'));

                        return;
                    }

                    case TransactionStatus.ERROR: {
                        notification.warn(t('paymentDetails:resultCode.refused'));

                        return;
                    }
                }

                setTransactionStatus(status);

                if (redirectUrl) {
                    // redirect to 3DS
                    window.location.replace(redirectUrl);

                    return;
                }

                // calls graphql submit if there is no redirection
                // otherwise callback will call journey later
                const result = await submitPorscheDeposit(token, agreements, false);

                const { giftVoucher } = result;

                if (giftVoucher.draftFlow.isDepositCompleted) {
                    dispatch({ type: 'next', token: result.token, giftVoucher });
                } else {
                    // there's an error
                    notification.warn(t('paymentDetails:resultCode.error'));
                }
            } finally {
                notification.destroy('primary');
            }
        },
        [dispatch, notification, submitPorscheDeposit, t, token],
        {}
    );

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={onSubmit}
            validate={state.giftVoucher.deposit.amount > 0 ? validation : null}
        >
            <Inner {...props} isSuccessful={isSuccessful} />
        </Formik>
    );
};

export default GiftVoucherPorschePaymentPage;
